import { auth } from './firebase';
import { getEnvVar } from '../utils/env';
import { requestCache } from '../utils/requestCache';
import { getApiBaseUrl } from '../utils/api';

const API_BASE_URL = getApiBaseUrl();

export interface AuthorizedUser {
  email: string;
  role: 'user' | 'admin' | 'superadmin' | 'read_only' | 'normal_user';
  createdAt: any;
  createdBy?: string;
  addedDate?: string;
  emailVerified?: boolean;
  uid?: string;
  planId?: string;
  status?: string;
  isTrialActive?: boolean;
  trialStartedAt?: any;
  trialEndsAt?: any;
  subscriptionStartedAt?: any;
  currentPeriodStart?: any;
  currentPeriodEnd?: any;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  propertyCount?: number;
  calculatedMonthlyPrice?: number;
}

export interface Account {
  id: string;
  name: string;
  description?: string;
  createdAt: any;
  createdBy: string;
  maxUsers?: number;
  userCount?: number;
  connectedUsers?: {
    email: string;
    type: 'owner' | 'shared';
    status?: string;
    role?: string;
  }[];
}

export interface UserAccount {
  email: string;
  accountId: string;
  assignedAt: any;
  assignedBy: string;
  role: string;
}

export interface ActivityLog {
  id: string;
  action: string;
  user: string;
  userEmail: string;
  timestamp: any;
  details?: string;
  metadata?: Record<string, any>;
}

export interface SystemSettings {
  appName: string;
  registrationMode: 'admin-only' | 'open' | 'closed';
  refreshInterval: number;
}

export interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
}

export interface FooterColumn {
  id: string;
  title: string;
  links: FooterLink[];
  order: number;
}

export interface SocialLink {
  platform: string;
  url: string;
  icon: string;
}

export interface FooterContent {
  brand: {
    appName: string;
    tagline: string;
    logoUrl?: string;
  };
  columns: FooterColumn[];
  social: SocialLink[];
  copyright: string;
  disclaimer: string;
  updatedAt?: any;
  updatedBy?: string;
  version?: number;
}

export interface FooterHistoryItem extends FooterContent {
  id: string;
  archivedAt: any;
  archivedBy: string;
  reason?: string;
}

export interface PageContent {
  slug: string;
  title: string;
  content: string;
  updatedAt?: any;
  updatedBy?: string;
  version: number;
}

export interface BlogPost extends PageContent {
  id: string;
  excerpt: string;
  publishedAt: string;
  author: string;
  imageUrl?: string;
  tags: string[];
  status: 'draft' | 'published';
}


class AdminService {
  /**
   * Get all authorized users
   */
  async getAuthorizedUsers(): Promise<AuthorizedUser[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/getAuthorizedUsers`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get authorized users');
      }

      const data = await response.json();
      return data.users || [];
    } catch (error) {
      console.error('Error getting authorized users:', error);
      throw error;
    }
  }

  /**
   * Add new authorized user
   */
  async addAuthorizedUser(email: string, role: 'user' | 'admin' | 'superadmin' | 'read_only', accountId?: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/addAuthorizedUser`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, role, accountId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add authorized user');
      }
    } catch (error) {
      console.error('Error adding authorized user:', error);
      throw error;
    }
  }

  /**
   * Update authorized user
   */
  async updateAuthorizedUser(email: string, updates: { role?: string; accountId?: string | null }): Promise<void> {
    try {
      if (!email || typeof email !== 'string' || !email.trim()) {
        throw new Error('Email parameter is required');
      }

      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/updateAuthorizedUser/${encodeURIComponent(email)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update authorized user');
      }
    } catch (error) {
      console.error('Error updating authorized user:', error);
      throw error;
    }
  }

  /**
   * Remove authorized user
   */
  async removeAuthorizedUser(email: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();
      const url = `${API_BASE_URL}/removeAuthorizedUser/${encodeURIComponent(email)}`;

      console.log('[AdminService.removeAuthorizedUser] Making DELETE request to:', url);

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      console.log('[AdminService.removeAuthorizedUser] Response status:', response.status);

      if (!response.ok) {
        const error = await response.json();
        console.error('[AdminService.removeAuthorizedUser] Error response:', error);
        throw new Error(error.error || 'Failed to remove authorized user');
      }

      console.log('[AdminService.removeAuthorizedUser] Success!');
    } catch (error) {
      console.error('[AdminService.removeAuthorizedUser] Error:', error);
      throw error;
    }
  }

  /**
   * Get user's account information
   */
  async getUserAccount(): Promise<any> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/getUserAccount`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get user account');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting user account:', error);
      throw error;
    }
  }

  /**
   * Get all accounts
   */
  async getAccounts(): Promise<Account[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/getAccounts`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get accounts');
      }

      const data = await response.json();
      return data.accounts || [];
    } catch (error) {
      console.error('Error getting accounts:', error);
      throw error;
    }
  }

  /**
   * Create new account
   */
  async createAccount(name: string, description?: string): Promise<Account> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/createAccount`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, description }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create account');
      }

      const data = await response.json();
      return data.account;
    } catch (error) {
      console.error('Error creating account:', error);
      throw error;
    }
  }

  /**
   * Delete account
   */
  async deleteAccount(accountId: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/deleteAccount/${accountId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete account');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  }

  /**
   * Get API usage for a specific user (admin only)
   */
  async getApiUsage(userId: string): Promise<any> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/getApiUsage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: { userId } }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get API usage');
      }

      const json = await response.json();
      
      if (json && typeof json === 'object' && 'result' in json) {
        return json.result;
      }
      
      return json;
    } catch (error) {
      console.error('Error getting API usage:', error);
      throw error;
    }
  }

  /**
   * Get API usage for an entire account (admin only)
   */
  async getAccountApiUsage(accountId: string): Promise<any> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/getApiUsage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: { accountId } }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get account API usage');
      }

      const json = await response.json();
      
      if (json && typeof json === 'object' && 'result' in json) {
        return json.result;
      }
      
      return json;
    } catch (error) {
      console.error('Error getting account API usage:', error);
      throw error;
    }
  }

  /**
   * Get all user API usage (admin only)
   */
  async getAllApiUsage(limit = 100): Promise<any[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/getAllApiUsage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: { limit } }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get all API usage');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting all API usage:', error);
      throw error;
    }
  }

  /**
   * Reset user API usage (admin only)
   */
  async resetApiUsage(userId: string): Promise<any> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/resetApiUsage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: { userId } }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to reset API usage');
      }

      return await response.json();
    } catch (error) {
      console.error('Error resetting API usage:', error);
      throw error;
    }
  }

  /**
   * Get user tracking and interest statistics (admin only)
   */
  async getUserTrackingStats(): Promise<any> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/getUserTrackingStats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get user tracking stats');
      }

      const data = await response.json();
      return data.stats;
    } catch (error) {
      console.error('Error getting user tracking stats:', error);
      throw error;
    }
  }

  /**
   * Assign user to account
   */
  async assignUserToAccount(email: string, accountId: string, accessType: 'owner' | 'read_only' | 'normal_user' = 'owner'): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/assignUserToAccount`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, accountId, accessType }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to assign user to account');
      }
    } catch (error) {
      console.error('Error assigning user to account:', error);
      throw error;
    }
  }

  /**
   * Unassign user from account
   */
  async unassignUser(email: string, accountId: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/unassignUser`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, accountId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to unassign user');
      }
    } catch (error) {
      console.error('Error unassigning user:', error);
      throw error;
    }
  }

  /**
   * Get activity logs
   */
  async getActivityLogs(limit = 50): Promise<ActivityLog[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/getActivityLogs?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get activity logs');
      }

      const data = await response.json();
      return data.logs || [];
    } catch (error) {
      console.error('Error getting activity logs:', error);
      throw error;
    }
  }

  /**
   * Clear activity logs
   */
  async clearActivityLogs(): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/clearActivityLogs`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to clear activity logs');
      }
    } catch (error) {
      console.error('Error clearing activity logs:', error);
      throw error;
    }
  }

  /**
   * Get system settings
   */
  async getSystemSettings(): Promise<SystemSettings> {
    // This would need backend implementation
    // For now, return defaults
    return {
      appName: 'CanvasEstate',
      registrationMode: 'admin-only',
      refreshInterval: 30
    };
  }

  /**
   * Save system settings
   */
  async saveSystemSettings(settings: SystemSettings): Promise<void> {
    // This would need backend implementation
    console.log('Saving settings:', settings);
  }

  /**
   * Log a user activity to the admin audit trail
   */
  async logActivity(action: string, details?: string, metadata?: Record<string, any>): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) return; // Silent fail for non-auth users

      const idToken = await user.getIdToken();

      await fetch(`${API_BASE_URL}/logUserActivity`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action, details, metadata }),
      });
    } catch (error) {
      // Log locally but don't break the UI
      console.warn('Silent failure logging activity to admin:', error);
    }
  }

  /**
   * Get footer content (public - can be called without auth for display)
   */
  async getFooterContent(): Promise<FooterContent> {
    return requestCache.fetch('footer-content', async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/getFooterContent`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to get footer content');
        }

        const data = await response.json();
        return data.content;
      } catch (error) {
        console.error('Error getting footer content:', error);
        throw error;
      }
    }, 30 * 60 * 1000); // 30 minute cache
  }

  /**
   * Update footer content (admin only)
   */
  async updateFooterContent(content: FooterContent): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/updateFooterContent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update footer content');
      }
    } catch (error) {
      console.error('Error updating footer content:', error);
      throw error;
    }
  }

  /**
   * Get footer content history (admin only)
   */
  async getFooterHistory(limit = 20): Promise<FooterHistoryItem[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/getFooterHistory?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get footer history');
      }

      const data = await response.json();
      return data.history || [];
    } catch (error) {
      console.error('Error getting footer history:', error);
      throw error;
    }
  }

  /**
   * Restore footer content from history (admin only)
   */
  async restoreFooterVersion(historyId: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/restoreFooterVersion`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ historyId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to restore footer version');
      }
    } catch (error) {
      console.error('Error restoring footer version:', error);
      throw error;
    }
  }

  /**
   * Get page content by slug
   */
  async getPageContent(slug: string): Promise<PageContent> {
    try {
      const response = await fetch(`${API_BASE_URL}/getPageContent?slug=${encodeURIComponent(slug)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // If 404, try to return fallback content for known pages
        if (response.status === 404) {
          const fallback = this.getFallbackPageContent(slug);
          if (fallback) return fallback;
          // Support both contact-us and contact-support
          if (slug === 'contact-support') return this.getFallbackPageContent('contact-us') as PageContent;
          throw new Error('Page not found');
        }
        const error = await response.json();
        throw new Error(error.error || 'Failed to get page content');
      }

      const data = await response.json();
      return data.content;
    } catch (error) {
      console.error(`Error getting page content for ${slug}:`, error);
      // Try fallback on network error or other failures
      const fallback = this.getFallbackPageContent(slug);
      if (fallback) return fallback;
      throw error;
    }
  }

  private getFallbackPageContent(slug: string): PageContent | null {
    const now = { seconds: Date.now() / 1000, nanoseconds: 0 } as any;

    if (slug === 'privacy-policy') {
      return {
        slug: 'privacy-policy',
        title: 'Privacy Policy',
        content: `# Privacy Policy

**Last Updated:** ${new Date().toLocaleDateString()}

**CanvasEstate** ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website (the "Platform").

Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.

---

## 1. Information We Collect

We collect information that identifies, relates to, describes, references, is capable of being associated with, or could reasonably be linked, directly or indirectly, with a particular consumer or device.

### 1.1 Personal Data
While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you ("Personal Data"). Personally identifiable information may include, but is not limited to:
- **Identity Data:** Name, username, date of birth.
- **Contact Data:** Email address, telephone number, billing address.
- **Financial Data:** Bank account details, property addresses, loan details, rental income, and expense records. **We do not store your banking login credentials.** We use secure third-party aggregators (e.g., Plaid, Yodlee) for bank connections.

### 1.2 Usage Data
We may also collect information how the Service is accessed and used ("Usage Data"). This Usage Data may include information such as your computer's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers and other diagnostic data.

### 1.3 Tracking & Cookies Data
We use cookies and similar tracking technologies to track the activity on our Service and hold certain information.
- **Session Cookies:** We use Session Cookies to operate our Service.
- **Preference Cookies:** We use Preference Cookies to remember your preferences and various settings.
- **Security Cookies:** We use Security Cookies for security purposes.

---

## 2. Uses of Your Data

CanvasEstate uses the collected data for various purposes:
- **To provide and maintain the Service:** Including monitoring the usage of our Service.
- **To manage your Account:** To manage your registration as a user of the Service.
- **To perform a contract:** The development, compliance and undertaking of the purchase contract for the products, items or services you have purchased or of any other contract with us through the Service.
- **To contact you:** To contact you by email, telephone calls, SMS, or other equivalent forms of electronic communication regarding updates or informative communications related to the functionalities, products or contracted services, including the security updates, when necessary or reasonable for their implementation.
- **To provide you with news:** Special offers and general information about other goods, services and events which we offer that are similar to those that you have already purchased or enquired about unless you have opted not to receive such information.
- **To manage your requests:** To attend and manage your requests to us.

---

## 3. Retention of Your Data

We will retain your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your Personal Data to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, and enforce our legal agreements and policies.

We will also retain Usage Data for internal analysis purposes. Usage Data is generally retained for a shorter period of time, except when this data is used to strengthen the security or to improve the functionality of our Service, or we are legally obligated to retain this data for longer time periods.

---

## 4. Transfer of Your Data

Your information, including Personal Data, is processed at the Company's operating offices and in any other places where the parties involved in the processing are located. It means that this information may be transferred to — and maintained on — computers located outside of your state, province, country or other governmental jurisdiction where the data protection laws may differ than those from your jurisdiction.

Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.

The Company will take all steps reasonably necessary to ensure that your data is treated securely and in accordance with this Privacy Policy and no transfer of your Personal Data will take place to an organization or a country unless there are adequate controls in place including the security of your data and other personal information.

---

## 5. Disclosure of Your Data

### 5.1 Business Transactions
If the Company is involved in a merger, acquisition or asset sale, your Personal Data may be transferred. We will provide notice before your Personal Data is transferred and becomes subject to a different Privacy Policy.

### 5.2 Law enforcement
Under certain circumstances, the Company may be required to disclose your Personal Data if required to do so by law or in response to valid requests by public authorities (e.g. a court or a government agency).

### 5.3 Other legal requirements
The Company may disclose your Personal Data in the good faith belief that such action is necessary to:
- Comply with a legal obligation
- Protect and defend the rights or property of the Company
- Prevent or investigate possible wrongdoing in connection with the Service
- Protect the personal safety of Users of the Service or the public
- Protect against legal liability

---

## 6. Security of Your Data

The security of your Personal Data is important to us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security. We implement standard security measures including hashing, encryption, and secure socket layer (SSL) technology.

---

## 7. Your Data Protection Rights Under GDPR / APP

We aim to take reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal Data.

If you wish to be informed what Personal Data we hold about you and if you want it to be removed from our systems, please contact us.

In certain circumstances, you have the following data protection rights:
- **The right to access, update or to delete the information we have on you.** Whenever made possible, you can access, update or request deletion of your Personal Data directly within your account settings section. If you are unable to perform these actions yourself, please contact us to assist you.
- **The right of rectification.** You have the right to have your information rectified if that information is inaccurate or incomplete.
- **The right to object.** You have the right to object to our processing of your Personal Data.
- **The right of restriction.** You have the right to request that we restrict the processing of your personal information.
- **The right to data portability.** You have the right to be provided with a copy of the information we have on you in a structured, machine-readable and commonly used format.
- **The right to withdraw consent.** You also have the right to withdraw your consent at any time where the Company relied on your consent to process your personal information.

---

## 8. Service Providers

We may employ third party companies and individuals to facilitate our Service ("Service Providers"), to provide the Service on our behalf, to perform Service-related services or to assist us in analyzing how our Service is used. These third parties have access to your Personal Data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.

### 8.1 Analytics
We may use third-party Service Providers to monitor and analyze the use of our Service.
- **Google Analytics:** Google Analytics is a web analytics service offered by Google that tracks and reports website traffic. Google uses the data collected to track and monitor the use of our Service. This data is shared with other Google services. Google may use the collected data to contextualize and personalize the ads of its own advertising network.

---

## 9. Payments

We may provide paid products and/or services within the Service. In that case, we use third-party services for payment processing (e.g. payment processors).

We will not store or collect your payment card details. That information is provided directly to our third-party payment processors whose use of your personal information is governed by their Privacy Policy. These payment processors adhere to the standards set by PCI-DSS as managed by the PCI Security Standards Council.

- **Stripe:** Their Privacy Policy can be viewed at https://stripe.com/us/privacy

---

## 10. Links to Other Sites

Our Service may contain links to other sites that are not operated by us. If you click on a third party link, you will be directed to that third party's site. We strongly advise you to review the Privacy Policy of every site you visit. We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.

---

## 11. Children's Privacy

Our Service does not address anyone under the age of 18 ("Children"). We do not knowingly collect personally identifiable information from anyone under the age of 18. If you are a parent or guardian and you are aware that your Children has provided us with Personal Data, please contact us. If we become aware that we have collected Personal Data from children without verification of parental consent, we take steps to remove that information from our servers.

---

## 12. Changes to This Privacy Policy

We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page. We will let you know via email and/or a prominent notice on our Service, prior to the change becoming effective and update the "effective date" at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.

---

## 13. Contact Us

If you have any questions about this Privacy Policy, please contact us:
- By email: **[admin@canvasestate.com](mailto:admin@canvasestate.com)**`,
        version: 3,
        updatedAt: now,
        updatedBy: 'system'
      };
    }

    if (slug === 'terms-of-service') {
      return {
        slug: 'terms-of-service',
        title: 'Terms of Service',
        content: `# Terms of Service

**Last Updated:** ${new Date().toLocaleDateString()}

Please read these Terms of Service ("Terms", "Terms of Service") carefully before using the **CanvasEstate** website (the "Service") operated by **CanvasEstate** ("us", "we", or "our").

Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms. These Terms apply to all visitors, users, and others who access or use the Service.

By accessing or using the Service you agree to be bound by these Terms. If you disagree with any part of the terms then you may not access the Service.

---

## 1. Accounts

When you create an account with us, you must provide us information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account on our Service.

You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password, whether your password is with our Service or a third-party service.

You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.

---

## 2. Subscriptions and Free Trials

Some parts of the Service are billed on a subscription basis ("Subscription(s)"). You will be billed in advance on a recurring and periodic basis ("Billing Cycle"). Billing cycles are set either on a monthly or annual basis, depending on the type of subscription plan you select when purchasing a Subscription.

### 2.1 Free Trial
CanvasEstate may, at its sole discretion, offer a Subscription with a free trial for a limited period of time ("Free Trial"). You may be required to enter your billing information in order to sign up for the Free Trial.
If you do enter your billing information when signing up for the Free Trial, you will not be charged by CanvasEstate until the Free Trial has expired. On the last day of the Free Trial period, unless you cancelled your Subscription, you will be automatically charged the applicable Subscription fees for the type of Subscription you have selected.

### 2.2 Fee Changes
CanvasEstate, in its sole discretion and at any time, may modify the Subscription fees for the Subscriptions. Any Subscription fee change will become effective at the end of the then-current Billing Cycle.

---

## 3. Financial Advice Disclaimer

**CANVASESATE IS NOT A FINANCIAL ADVISOR.**
The content provided on the Service, including but not limited to property valuations, cash flow analysis, and market data, is for informational purposes only. It does not constitute professional financial, legal, or real estate advice.

You should consult with a qualified professional (e.g., financial planner, accountant, or buyer's agent) before making any investment decisions. We do not guarantee the accuracy, completeness, or usefulness of any information on the Service.

---

## 4. Intellectual Property

The Service and its original content (excluding Content provided by users), features and functionality are and will remain the exclusive property of CanvasEstate and its licensors. The Service is protected by copyright, trademark, and other laws of both Australia and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of CanvasEstate.

---

## 5. Links To Other Web Sites

Our Service may contain links to third-party web sites or services that are not owned or controlled by CanvasEstate.

CanvasEstate has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third party web sites or services. You further acknowledge and agree that CanvasEstate shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with use of or reliance on any such content, goods or services available on or through any such web sites or services.

---

## 6. Termination

We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.

Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service.

---

## 7. Limitation of Liability

In no event shall CanvasEstate, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from (i) your access to or use of or inability to access or use the Service; (ii) any conduct or content of any third party on the Service; (iii) any content obtained from the Service; and (iv) unauthorized access, use or alteration of your transmissions or content, whether based on warranty, contract, tort (including negligence) or any other legal theory, whether or not we have been informed of the possibility of such damage, and even if a remedy set forth herein is found to have failed of its essential purpose.

---

## 8. Governing Law

These Terms shall be governed and construed in accordance with the laws of New South Wales, Australia, without regard to its conflict of law provisions.

Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect. These Terms constitute the entire agreement between us regarding our Service, and supersede and replace any prior agreements we might have between us regarding the Service.

---

## 9. Changes

We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material we will try to provide at least 30 days notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.

By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the Service.

---

## 10. Contact Us

If you have any questions about these Terms, please contact us:
- By email: **[admin@canvasestate.com](mailto:admin@canvasestate.com)**`,
        version: 1,
        updatedAt: now,
        updatedBy: 'system'
      };
    }


    if (slug === 'cookie-policy') {
      return {
        slug: 'cookie-policy',
        title: 'Cookie Policy',
        content: `
# Cookie Policy

**Last Updated:** ${new Date().toLocaleDateString()}

This Cookie Policy explains how **CanvasEstate** ("we", "us", and "our") uses cookies and similar technologies to recognise you when you visit our Platform.

---

## 1. What are Cookies?

Cookies are small data files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and to provide reporting information.

## 2. How We Use Cookies

We use cookies for the following purposes:

### 2.1 Essential Cookies
These cookies are strictly necessary for the website to function. They allow you to:
- Log in to your secure account.
- Navigate between pages without losing your session.
- Ensure the security of your connection.

### 2.2 Analytics Cookies
We use these cookies to collect information about how you interact with our Platform (e.g., Google Analytics). We use this data to:
- Analyse user behaviour and improve website performance.
- Track legitimate usage for security monitoring.

### 2.3 Functional Cookies
These cookies allow us to remember the choices you make (such as your user name, language, or the region you are in) and provide enhanced, more personal features.

---

## 3. Controlling Cookies

You have the right to decide whether to accept or reject cookies. You can set or amend your web browser controls to accept or refuse cookies. If you choose to reject cookies, you may still use our website though your access to some functionality and areas of our website may be restricted.

**Contact Us**
If you have any questions about our use of cookies, please email us at **admin@canvasestate.com**.
        `,
        version: 1,
        updatedAt: now,
        updatedBy: 'system'
      };
    }

    if (slug === 'refund-policy') {
      return {
        slug: 'refund-policy',
        title: 'Refund Policy',
        content: `
# Refund Policy

**Last Updated:** ${new Date().toLocaleDateString()}

At **CanvasEstate**, we strive to provide transparent and fair billing practices. This Refund Policy outlines the terms under which refunds may be granted for our subscription services.

---

## 1. Subscription Cancellations

- **Monthly & Annual Plans:** You can cancel your subscription at any time via your account settings.
- **Effect of Cancellation:** Your cancellation will take effect at the end of the current paid term. You will retain access to premium features until that date.
- **No Partial Refunds:** We generally do not offer refunds or credits for partial months or years of service, except as required by Australian Consumer Law.

## 2. Australian Consumer Law

Our services come with guarantees that cannot be excluded under the Australian Consumer Law. You are entitled to a replacement or refund for a major failure and compensation for any other reasonably foreseeable loss or damage.

## 3. Requesting a Refund

If you believe you have been charged in error or have encountered a major service failure, please contact our support team immediately.

- **Email:** admin@canvasestate.com
- **Subject:** Refund Request - [Your Account Email]

We review all refund requests on a case-by-case basis and will respond within 5 business days.

---

**Contact Us**
If you have any questions about this Refund Policy, please contact us at **admin@canvasestate.com**.
        `,
        version: 1,
        updatedAt: now,
        updatedBy: 'system'
      };
    }


    if (slug === 'help-center') {
      return {
        slug: 'help-center',
        title: 'Help Center',
        content: `
# Help Center

Welcome to the CanvasEstate Help Center. Find answers to common questions and learn how to master your property portfolio.

## Getting Started

- **Adding your first property**: Navigate to the Properties tab and click "Add Property". You'll need the purchase price, date, and basic details.
- **Understanding the Dashboard**: Your dashboard provides a "Big Picture" view of your Net Wealth, Total Equity, and Portfolio Yield.

## Portfolio Management

- **Updating Valuations**: We recommend updating property valuations every 6-12 months. You can add Market Valuations (based on recent sales) or Bank Valuations.
- **Tracking Expenses**: Categorise your expenses (rates, maintenance, insurance) to calculate accurate net rental yields.

## Market Data Pulse

- **What is Market Pulse?**: Our integrated blog provides weekly insights into the Australian property market, including auction results and interest rate trends.

## Need more help?

If you couldn't find the answer you were looking for, please check our [Documentation](/pages/documentation) or [Contact Support](/pages/contact-us).
        `,
        version: 1,
        updatedAt: now,
        updatedBy: 'system'
      };
    }

    if (slug === 'documentation') {
      return {
        slug: 'documentation',
        title: 'Documentation',
        content: `
# Documentation

Comprehensive guides and references for the CanvasEstate platform.

### Platform Overview

CanvasEstate is a property investment management solution designed to help investors track, analyse, and optimise their real estate portfolios.

### Key Features

*   **Dashboard**: A high-level view of your portfolio performance, including total value, equity, and cash flow.
*   **Property Tracking**: Detailed records for each property, including purchase details, valuation history, and documents.
*   **Financial Analysis**: Tools to analyse rental yield, cash flow, and growth projections.
*   **Reports**: Generate professional reports for tax purposes or personal record-keeping.

## User Guides

### Managing Properties

1.  **Adding a Property**: Click "Add Property" and follow the intuitive wizard.
2.  **Updating Valuations**: Regular valuations help track equity growth over time.
3.  **Uploading Documents**: Store contracts, receipts, and statements securely. Supports PDF, JPG, and PNG formats.

### Financial Tools

*   **Loan Calculator**: Estimate repayments and interest costs based on current Australian rates.
*   **Growth Projector**: Visualise potential portfolio value over 10, 20, or 30 years.

## Technical Support

For technical issues or bug reports, please contact our support team via the [Contact Us](/pages/contact-us) page.
        `,
        version: 1,
        updatedAt: now,
        updatedBy: 'system'
      };
    }

    if (slug === 'contact-us' || slug === 'contact-support') {
      return {
        slug: 'contact-us',
        title: 'Contact Us',
        content: `
# Contact Us

We're here to help! Whether you have a question about the platform, need technical support, or want to share feedback, our team is ready to assist.

---

## 👋 Meet the Team

**CanvasEstate** was founded by Armin Imani and Parisa Esmaeil, two experienced technology professionals with a passion for property investment. Our mission is to empower Australian property investors with institutional-grade tools and insights.

- **Armin Imani** — *Founder & CEO*  
  Former Information Security Architect with Big 4 consulting and major financial institutions. Armin brings deep expertise in secure systems design and fintech scaling.

- **Parisa Esmaeil** — *CTO & Co-founder*  
  Accomplished Software Engineer with experience building digital infrastructure for Australia's largest companies. Parisa leads the technical architecture and UI/UX design of the platform.

[Learn more about our team →](/pages/about-us)

---

## 📧 Email Support

**General Inquiries & Support:**  
[admin@canvasestate.com](mailto:admin@canvasestate.com)

**Technical Support:**  
[support@canvasestate.com](mailto:support@canvasestate.com)

**Media & Press:**  
[media@canvasestate.com](mailto:media@canvasestate.com)

We aim to respond to all inquiries within **24-48 business hours**.

---

## 🕐 Business Hours

| Day | Hours |
|-----|-------|
| Monday – Friday | 9:00 AM – 5:00 PM AEST |
| Saturday | Closed |
| Sunday | Closed |

*Note: Emails received outside business hours will be responded to on the next business day.*

---

## 📍 Our Location

**CanvasEstate**  
Operated by **Jaytoon Pty Ltd** (ABN: 58 642 670 237)  
Sydney, NSW  
Australia

We're a proudly Australian company serving property investors across the country and internationally.

---

## 🤝 Partnership & Business Inquiries

Are you a:
- **Mortgage Broker** looking to add value for your clients?
- **Property Buyer's Agent** wanting better portfolio insights?
- **Financial Planner** seeking property analysis tools?
- **Developer** interested in API integrations?

We'd love to explore partnership opportunities. Email us at **partnerships@canvasestate.com** with details about your business and how you envision working together.

---

## 💡 Frequently Asked Questions

Before reaching out, you might find your answer in our resources:

| Resource | Description |
|----------|-------------|
| [Help Center](/pages/help-center) | Getting started guides and common questions |
| [Documentation](/pages/documentation) | Detailed platform documentation |
| [Pricing](/pricing) | Plans, features, and subscription details |
| [Community Forum](/community) | Connect with other property investors |

---

## 💬 Feedback & Suggestions

We're constantly improving CanvasEstate based on user feedback. If you have ideas for new features or improvements, we'd love to hear them!

**How to submit feedback:**
1. Email us at **admin@canvasestate.com**
2. Use the subject line **"Feature Request"** or **"Feedback"**
3. Tell us what you'd like to see or how we can improve

All feedback is reviewed by our product team and helps shape the future of CanvasEstate.

---

## 🐛 Report a Bug

Found something not working as expected? Help us fix it:

1. Email **support@canvasestate.com**
2. Use subject line **"Bug Report"**
3. Include:
   - What you were trying to do
   - What happened instead
   - Browser and device you were using
   - Screenshots if possible

---

## 🔐 Security Concerns

If you've discovered a security vulnerability, please report it responsibly:

**Security Email:** [security@canvasestate.com](mailto:security@canvasestate.com)

We take security seriously and will respond to verified reports within 24 hours.

---

*Thank you for choosing CanvasEstate. We're committed to helping you succeed in your property investment journey.*
        `,
        version: 1,
        updatedAt: now,
        updatedBy: 'system'
      };
    }

    if (slug === 'about-us') {
      return {
        slug: 'about-us',
        title: 'About Us',
        content: `
# About CanvasEstate

**CanvasEstate** is an Australian property investment management platform designed to help property investors make smarter, data-driven decisions about their portfolios.

---

## 🏢 Operated by Jaytoon Pty Ltd
CanvasEstate is a trading name of **Jaytoon Pty Ltd** (ABN: **58 642 670 237**). Based in Sydney, we are committed to providing professional-grade tools for the Australian property market.

---

## Our Story

CanvasEstate was founded in 2024 by Australian property investors who experienced firsthand the challenges of managing a growing property portfolio. Tracking equity, cash flow, rental yields, and loan details across multiple properties using spreadsheets quickly became unsustainable.

We built CanvasEstate to solve this problem — a single, intuitive platform that brings all your property data together in one place, providing the insights you need to make informed investment decisions.

---

## Our Mission

> **To empower Australian property investors with the data, tools, and insights they need to take control of their financial future.**

We believe that every property investor, whether they own one property or twenty, deserves access to professional-grade portfolio management tools without the complexity or high costs of traditional wealth management software.

---

## What We Offer

### 📊 Portfolio Dashboard
Get a complete "Big Picture" view of your property portfolio, including total value, equity, net wealth, and cash flow — all updated in real-time.

### 📈 Financial Analysis
Understand your true financial position with detailed rental yield calculations, expense tracking, and comprehensive financial reports ready for tax time.

### 🏠 Document Management
Store and organise all your property documents — contracts, valuations, receipts, and statements — securely in one place.

### 📰 Market Insights
Stay ahead with curated market research, RBA interest rate updates, and property market analysis from our research team.

### 🤝 Advisor Collaboration
Share read-only access with your accountant, mortgage broker, or financial advisor. They see exactly what they need, when they need it.

---

## Security & Privacy

Your data security is our top priority. CanvasEstate is built on enterprise-grade infrastructure with:

- **Bank-level encryption** for all data in transit and at rest
- **Multi-factor authentication** to protect your account
- **Australian data residency** — your data stays in Australia
- **Regular security audits** and compliance reviews

---

## Our Team

CanvasEstate is built by a small, dedicated team based in Sydney, Australia. We're property investors ourselves, and we use CanvasEstate every day to manage our own portfolios.

We're passionate about helping fellow investors succeed and are always looking for feedback on how we can improve the platform.

---

## Contact Us

We'd love to hear from you — whether you have feedback, questions, or just want to say hello.

📧 **Email:** [admin@canvasestate.com](mailto:admin@canvasestate.com)

📍 **Location:** Sydney, NSW, Australia

---

*CanvasEstate is an Australian company committed to helping local investors succeed.*
        `,
        version: 1,
        updatedAt: now,
        updatedBy: 'system'
      };
    }

    return null;
  }

  /**
   * Save page content (admin only)
   */
  async savePageContent(slug: string, content: { title: string; content: string }): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/updatePageContent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ slug, content }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save page content');
      }
    } catch (error) {
      console.error('Error saving page content:', error);
      throw error;
    }
  }
  /**
   * Get all blog posts
   */
  async getBlogPosts(): Promise<BlogPost[]> {
    try {
      // Try to get from API first (mocked for now)
      // const response = await fetch(`${API_BASE_URL}/getBlogPosts`);
      // if (!response.ok) throw new Error('Failed to fetch posts');
      // return await response.json();

      // Fallback to local storage for development
      const storedPosts = localStorage.getItem('blog_posts');
      if (storedPosts) {
        return JSON.parse(storedPosts);
      }

      // Default sample posts
      const samplePosts: BlogPost[] = [
        {
          id: '1',
          slug: 'rba-december-2024-cash-rate-decision-what-it-means-for-property-investors',
          title: 'RBA December 2024 Cash Rate Decision: What It Means for Property Investors',
          excerpt: 'The Reserve Bank has held rates steady at 4.35% in December 2024, signalling a potential turning point for the property market. Here\'s what investors need to know.',
          content: `# RBA December 2024 Cash Rate Decision: What It Means for Property Investors

On December 10, 2024, the Reserve Bank of Australia (RBA) Board decided to leave the cash rate target unchanged at **4.35 per cent**. This marks the ninth consecutive meeting without a rate change, but the accompanying statement revealed important shifts in the RBA's outlook that property investors should carefully consider.

## The Key Takeaways

### Inflation is Easing, But Still Above Target

The RBA noted that inflation has fallen substantially since the 2022 peak. However, underlying inflation remains around 3.5%, still above the RBA's target midpoint of 2.5%. The Board projects inflation won't sustainably return to target until 2026.

**What this means for investors:** While we're not out of the woods yet, the inflation trajectory is heading in the right direction. This suggests the peak of the rate hiking cycle is behind us.

### Economic Growth is Weak

The September quarter national accounts showed the economy grew by only **0.8% over the past year**. Outside of the COVID-19 pandemic, this is the slowest pace of growth since the early 1990s. Household consumption spending remains subdued, particularly on discretionary items.

**What this means for investors:** Weaker economic conditions typically put downward pressure on property prices in the short term, but also increase the likelihood of rate cuts in 2025.

### Labour Market Remains Resilient

Despite the economic weakness, the labour market has held up better than expected:
- Unemployment rate: 4.1% (up from 3.5% in late 2022)
- Strong employment growth over recent months
- Participation rate near record highs

**What this means for investors:** A resilient job market helps support rental demand and reduces the risk of widespread mortgage defaults.

## What Does This Mean for Property Prices?

### Short-Term Outlook (Next 6 Months)

The property market is likely to remain in a consolidation phase. With rates on hold and consumer confidence subdued, we expect:

- **Sydney & Melbourne:** Modest price growth of 2-4%
- **Brisbane & Perth:** Continued outperformance with 5-7% growth
- **Adelaide:** Steady growth around 4-5%
- **Regional markets:** Mixed performance, with lifestyle areas holding value

### Medium-Term Outlook (2025-2026)

The critical factor is when and by how much the RBA cuts rates. Market economists are increasingly pricing in rate cuts from mid-2025. Each 0.25% cut typically:

- Increases borrowing capacity by approximately 2-3%
- Adds momentum to buyer sentiment
- Historically correlates with 1-2% property price increases

## Strategic Considerations for Investors

### 1. Fixed vs Variable Rate Decisions

With the RBA signalling confidence that inflation is moving toward target, the case for fixing rates has weakened. However, consider:

- **Variable rates:** Position you to benefit immediately when cuts occur
- **Fixed rates:** May offer short-term savings if you can secure rates below current variable rates
- **Split loans:** Provide a balanced approach

### 2. Timing Your Next Purchase

If you're looking to buy, the next 6-12 months could present opportunities:

- Less competition as some buyers wait for rate cuts
- Vendors may be more motivated to negotiate
- Pre-approval now locks in your borrowing capacity before rate cuts increase competition

### 3. Portfolio Review

This is an excellent time to:
- Review your loan structures and interest rates
- Consider refinancing to a more competitive rate
- Assess whether your portfolio allocation aligns with emerging growth corridors

## The Bottom Line

The December 2024 RBA decision maintains the status quo, but the accompanying commentary suggests we're approaching a turning point. The Board is "gaining some confidence that inflation is moving sustainably towards target" - language that historically precedes a shift in policy direction.

For property investors, this means:

1. **Don't panic sell** - the worst of the rate cycle appears to be behind us
2. **Position for opportunity** - start preparing for the next growth cycle
3. **Focus on fundamentals** - properties in supply-constrained areas with strong rental demand will outperform
4. **Stay liquid** - having access to funds when opportunities arise will be crucial

The property market rewards those who plan strategically and act decisively. With rates likely to fall in 2025, those who position themselves now could be well-placed for the next growth phase.

---

*This article was written on December 5, 2024, and reflects current market conditions. Always consult with qualified financial advisors before making investment decisions.*`,
          author: 'CanvasEstate Research Team',
          publishedAt: new Date(Date.now() - 86400000 * 1).toISOString(), // 1 day ago
          tags: ['RBA', 'Interest Rates', 'Market Analysis', 'Investment Strategy'],
          status: 'published',
          version: 1,
          updatedAt: { seconds: Date.now() / 1000, nanoseconds: 0 },
          updatedBy: 'system'
        },
        {
          id: '2',
          slug: 'australian-property-market-state-by-state-december-2024',
          title: 'Australian Property Market State-by-State: December 2024 Analysis',
          excerpt: 'A comprehensive breakdown of property market performance across all Australian states and territories, including median prices, growth trends, and investment opportunities.',
          content: `# Australian Property Market State-by-State: December 2024 Analysis

The Australian property market continues to demonstrate remarkable regional diversity, with some markets posting double-digit growth while others consolidate after years of strong performance. This comprehensive analysis breaks down the current state of play across every Australian state and territory.

## National Overview

Australia's total residential dwelling stock is valued at approximately **$10.7 trillion**, with a national mean dwelling price of around **$970,000**. The market has shown resilience despite 13 rate hikes since May 2022, with most capital cities recording positive growth over the past 12 months.

---

## New South Wales

### Sydney
- **Median House Price:** $1.45 million
- **12-Month Change:** +5.2%
- **Rental Yield (Houses):** 2.8%

Sydney remains Australia's most expensive property market, but growth has moderated from the double-digit gains of 2023. The inner-city and eastern suburbs have seen the strongest performance, while outer western suburbs have experienced more modest growth.

**Key suburbs to watch:**
- **Parramatta:** Infrastructure investment driving growth
- **Inner West:** Strong demand for lifestyle suburbs
- **Northern Beaches:** Limited supply supporting prices

### Regional NSW
Regional NSW has stabilised after the pandemic-driven boom. Areas within commuting distance of Sydney, such as the Central Coast and Wollongong, continue to perform well. However, more remote areas like the North Coast have seen price corrections of 5-10%.

---

## Victoria

### Melbourne
- **Median House Price:** $1.03 million
- **12-Month Change:** +2.8%
- **Rental Yield (Houses):** 3.1%

Melbourne has been one of the more subdued capital city markets, impacted by higher supply levels and population growth that lagged other states during COVID. However, value is emerging:

**Key suburbs to watch:**
- **South-East corridor:** Affordable entry points with infrastructure investment
- **Inner-city apartments:** Rental yields improving as international students return
- **Western suburbs:** Strong population growth

### Regional Victoria
Geelong and Ballarat remain popular lifestyle alternatives to Melbourne, with median prices around 40-50% below Melbourne. These markets offer better rental yields, typically 4-5%.

---

## Queensland

### Brisbane
- **Median House Price:** $920,000
- **12-Month Change:** +12.1%
- **Rental Yield (Houses):** 3.9%

Brisbane has been the standout performer among capital cities, driven by:
- Strong interstate migration (net +40,000 per year)
- 2032 Olympics infrastructure investment
- Relative affordability compared to Sydney and Melbourne
- Chronic undersupply of housing

**Key suburbs to watch:**
- **Fortitude Valley/Newstead:** Urban regeneration hotspots
- **Northside suburbs:** Benefiting from Cross River Rail
- **Ipswich corridor:** Affordable growth area

### Gold Coast & Sunshine Coast
Both coastal markets have seen extraordinary growth, with the Gold Coast median house price now exceeding $950,000. The 2032 Olympics will bring significant infrastructure benefits to the Gold Coast.

---

## Western Australia

### Perth
- **Median House Price:** $750,000
- **12-Month Change:** +19.2%
- **Rental Yield (Houses):** 4.5%

Perth has emerged as Australia's fastest-growing property market, after years of underperformance following the mining downturn. Key drivers include:
- Chronic rental shortage (vacancy rate < 0.5%)
- Mining sector recovery and expansion
- Relative affordability attracting eastern states investors
- Strong population growth

**Key suburbs to watch:**
- **Northern corridor (Joondalup, Wanneroo):** Strong family demographics
- **South-East corridor:** Infrastructure investment
- **Fremantle:** Lifestyle appeal with heritage character

---

## South Australia

### Adelaide
- **Median House Price:** $780,000
- **12-Month Change:** +14.5%
- **Rental Yield (Houses):** 4.2%

Adelaide has broken out of its historical role as a "slow and steady" market, posting impressive growth driven by:
- Defence industry investment (AUKUS submarine program)
- Relative affordability attracting first-home buyers
- Interstate migration from Victoria
- Limited new housing supply

**Key suburbs to watch:**
- **Port Adelaide:** Significant urban renewal underway
- **Adelaide Hills:** Lifestyle appeal
- **Northern suburbs:** Defence industry proximity

---

## Tasmania

### Hobart
- **Median House Price:** $680,000
- **12-Month Change:** -2.1%
- **Rental Yield (Houses):** 4.4%

Hobart has experienced price corrections after being Australia's best-performing market during 2020-2022. The market is now consolidating, with reduced buyer activity from interstate investors.

**Outlook:** Expect stabilisation through 2025, with modest growth resuming as affordability improves.

---

## Australian Capital Territory

### Canberra
- **Median House Price:** $1.02 million
- **12-Month Change:** +3.5%
- **Rental Yield (Houses):** 3.5%

Canberra remains one of Australia's most stable markets, underpinned by public sector employment. Record prices were set in several suburbs during late 2024, including Duffy, which recently broke its suburb record for the second time.

---

## Northern Territory

### Darwin
- **Median House Price:** $550,000
- **12-Month Change:** +6.8%
- **Rental Yield (Houses):** 5.8%

Darwin offers Australia's highest rental yields among capital cities, making it attractive for cash-flow focused investors. The market has recovered from the post-mining downturn, with defence spending and gas projects supporting growth.

---

## Investment Strategy by Market

### For Capital Growth Focus:
1. **Brisbane** - Best combination of affordability, infrastructure investment, and population growth
2. **Perth** - Strong momentum but higher risk of correction
3. **Adelaide** - Solid fundamentals with defence industry tailwind

### For Cash Flow Focus:
1. **Darwin** - Highest yields, improving fundamentals
2. **Perth** - Strong yields with capital growth potential
3. **Adelaide** - Good balance of yield and growth

### For Diversification:
Consider splitting your portfolio across 2-3 markets to reduce concentration risk. A common strategy is:
- 50% in a growth market (Brisbane/Perth)
- 30% in a stable market (Sydney/Melbourne)
- 20% in a yield market (Adelaide/Darwin)

---

## The Bottom Line

The Australian property market in December 2024 offers diverse opportunities across different states and price points. The key themes to monitor include:

1. **Interest rate trajectory** - cuts in 2025 will boost all markets
2. **Population growth** - particularly interstate migration patterns
3. **Housing supply** - chronic undersupply in many markets supports prices
4. **Infrastructure investment** - Olympics, defence, and transport projects

Success in property investment comes from understanding these macro trends and applying them to specific local markets. Take time to research your target markets thoroughly before making decisions.

---

*Market data current as of December 2024. Sources include CoreLogic, Domain, and ABS. Always conduct your own due diligence before investing.*`,
          author: 'CanvasEstate Research Team',
          publishedAt: new Date(Date.now() - 86400000 * 3).toISOString(), // 3 days ago
          tags: ['Market Analysis', 'State Markets', 'Property Prices', 'Investment'],
          status: 'published',
          version: 1,
          updatedAt: { seconds: Date.now() / 1000, nanoseconds: 0 },
          updatedBy: 'system'
        },
        {
          id: '3',
          slug: 'understanding-rental-yield-complete-guide-2024',
          title: 'Understanding Rental Yield: The Complete Australian Investor\'s Guide',
          excerpt: 'Master the fundamentals of rental yield calculation, including gross vs net yield, typical yields by city, and how to use yield analysis in your investment decisions.',
          content: `# Understanding Rental Yield: The Complete Australian Investor's Guide

Rental yield is one of the most important metrics for property investors, yet it's frequently misunderstood or calculated incorrectly. This comprehensive guide will help you master rental yield analysis and apply it effectively to your investment decisions.

## What is Rental Yield?

Rental yield measures the annual return from rent as a percentage of the property's value. It's expressed as a percentage and helps investors compare the income-generating potential of different properties.

**The Basic Formula:**
\`\`\`
Rental Yield = (Annual Rental Income / Property Value) × 100
\`\`\`

For example, a property worth $600,000 that generates $30,000 per year in rent has a rental yield of 5%.

---

## Gross Yield vs Net Yield: What's the Difference?

Understanding the difference between gross and net yield is crucial for making informed investment decisions.

### Gross Rental Yield

Gross yield is the simpler calculation that most people use when discussing rental yields. It considers only the rental income and property value.

**Gross Yield Formula:**
\`\`\`
Gross Yield = (Annual Rent / Purchase Price) × 100
\`\`\`

**Example:**
- Property Price: $500,000
- Weekly Rent: $500
- Annual Rent: $500 × 52 = $26,000
- Gross Yield: ($26,000 / $500,000) × 100 = **5.2%**

### Net Rental Yield

Net yield provides a more accurate picture by accounting for all property-related expenses. This is what you actually take home.

**Net Yield Formula:**
\`\`\`
Net Yield = ((Annual Rent - Annual Expenses) / Total Investment Cost) × 100
\`\`\`

**Typical Expenses to Include:**
| Expense Type | Typical Amount |
|-------------|----------------|
| Property Management | 7-10% of rent |
| Council Rates | $1,500-3,000/year |
| Water Rates | $800-1,500/year |
| Insurance | $1,200-2,500/year |
| Maintenance | 1-2% of property value |
| Body Corporate (apartments) | $3,000-8,000/year |
| Vacancy Allowance | 2-4 weeks/year |
| Land Tax (if applicable) | Varies by state |

**Example:**
- Property Price: $500,000
- Stamp Duty: $18,000
- Legal/Inspection Costs: $2,000
- **Total Investment: $520,000**

- Annual Rent: $26,000
- Less Property Management (8%): $2,080
- Less Council Rates: $2,000
- Less Insurance: $1,500
- Less Maintenance: $2,000
- Less Vacancy (2 weeks): $1,000
- **Net Annual Income: $17,420**

- Net Yield: ($17,420 / $520,000) × 100 = **3.35%**

As you can see, the net yield (3.35%) is significantly lower than the gross yield (5.2%). Always calculate net yield when evaluating investments.

---

## Current Rental Yields Across Australian Capital Cities

Here's how rental yields compare across Australian capital cities as of December 2024:

### Houses

| City | Median Price | Median Weekly Rent | Gross Yield |
|------|-------------|-------------------|-------------|
| Sydney | $1,450,000 | $780 | 2.8% |
| Melbourne | $1,030,000 | $620 | 3.1% |
| Brisbane | $920,000 | $680 | 3.9% |
| Perth | $750,000 | $650 | 4.5% |
| Adelaide | $780,000 | $630 | 4.2% |
| Hobart | $680,000 | $580 | 4.4% |
| Darwin | $550,000 | $620 | 5.8% |
| Canberra | $1,020,000 | $700 | 3.5% |

### Apartments

| City | Median Price | Median Weekly Rent | Gross Yield |
|------|-------------|-------------------|-------------|
| Sydney | $800,000 | $650 | 4.2% |
| Melbourne | $550,000 | $520 | 4.9% |
| Brisbane | $500,000 | $550 | 5.7% |
| Perth | $450,000 | $550 | 6.3% |
| Adelaide | $420,000 | $480 | 5.9% |

---

## What is a Good Rental Yield?

The "right" rental yield depends on your investment strategy:

### Capital Growth Strategy (Lower Yields Acceptable)
If you're primarily investing for capital growth, you might accept yields of 2-4%. Properties in blue-chip suburbs often have lower yields but stronger long-term price appreciation.

**Typical Properties:**
- Inner-city houses in Sydney/Melbourne
- Established suburbs with limited land availability
- Properties near CBD or major employment hubs

### Balanced Strategy (Moderate Yields)
A balanced approach targets yields of 4-5.5%, aiming for both reasonable cash flow and capital growth potential.

**Typical Properties:**
- Middle-ring suburbs in growth cities
- Properties in Brisbane, Adelaide, Perth
- Well-located apartments in capital cities

### Cash Flow Strategy (Higher Yields Required)
Investors prioritising income target yields above 5.5%. These properties typically have less capital growth potential.

**Typical Properties:**
- Regional areas with strong employment
- Mining towns (higher risk)
- Darwin and Perth apartments
- Multi-dwelling properties

---

## The Yield vs Growth Trade-Off

One of the fundamental truths in property investing is the inverse relationship between yield and growth:

**Higher Yield Properties Tend to Have:**
- Lower capital growth
- Higher tenant turnover
- More maintenance requirements
- Greater exposure to economic cycles

**Lower Yield Properties Tend to Have:**
- Stronger capital growth
- Better quality tenants
- Land value appreciation
- More stable long-term returns

### Case Study: Sydney vs Darwin

Let's compare two $600,000 investments over 10 years:

**Sydney (Low Yield, High Growth):**
- Initial Gross Yield: 3%
- Annual Capital Growth: 6%
- After 10 years: Property worth $1,074,000
- Total rental income (approximate): $234,000
- **Total Return: $708,000**

**Darwin (High Yield, Lower Growth):**
- Initial Gross Yield: 5.5%
- Annual Capital Growth: 3%
- After 10 years: Property worth $806,000
- Total rental income (approximate): $396,000
- **Total Return: $602,000**

In this example, the lower-yield Sydney property generated a better total return despite providing less rental income along the way.

---

## How to Increase Rental Yield

If you already own investment properties, here are strategies to improve your rental yield:

### 1. Conduct Market Rent Reviews
Many landlords leave money on the table by not regularly reviewing market rents. Check comparable rentals every 6-12 months and adjust accordingly.

### 2. Add Value Through Renovations
Strategic renovations can justify rent increases:
- Kitchen and bathroom updates
- Adding air conditioning
- Outdoor entertaining areas
- Home office spaces (increasingly valuable post-COVID)

### 3. Reduce Expenses
- Shop around for landlord insurance annually
- Review property management fees
- Implement preventive maintenance to avoid costly repairs
- Consider self-management if you have time (saves 7-10%)

### 4. Reduce Vacancy
- Price rent slightly below market for quick tenant placement
- Maintain the property well to retain good tenants
- Be responsive to tenant requests
- Consider pet-friendly policies

### 5. Consider Dual Income Strategies
- Granny flat additions (subject to council approval)
- Room-by-room rentals
- Airbnb for suitable properties

---

## Common Rental Yield Mistakes to Avoid

### 1. Using Purchase Price Instead of Current Value
When tracking your portfolio performance, use current market value for yield calculations, not your original purchase price.

### 2. Forgetting About Vacancy
Even well-managed properties experience vacancy between tenants. Always factor in 2-4 weeks vacancy per year.

### 3. Ignoring Land Tax
Land tax can significantly impact your net yield, especially in NSW and Victoria. Make sure to include it in your calculations.

### 4. Chasing Yield Alone
High yields often come with higher risks. A 9% yield in a struggling regional town may be worse than a 4% yield in a growth capital city.

### 5. Not Accounting for All Purchase Costs
Include stamp duty, legal fees, and inspection costs in your total investment cost for accurate net yield calculations.

---

## Using Rental Yield in Investment Decisions

Rental yield should be one of several factors in your investment decision, not the only factor. Consider:

1. **Location quality** - Is the area improving or declining?
2. **Tenant demand** - What's the vacancy rate in the area?
3. **Capital growth history** - What are the long-term price trends?
4. **Supply and demand** - Is there new development nearby?
5. **Economic drivers** - What supports employment in the area?

The best investments often combine reasonable yields with strong growth fundamentals.

---

## Key Takeaways

1. **Always calculate net yield** - It gives you the true picture of your return
2. **Understand the yield-growth trade-off** - Higher yields often mean lower growth
3. **Compare apples with apples** - Use the same methodology when comparing properties
4. **Factor in all costs** - Including purchase costs and ongoing expenses
5. **Don't chase yield alone** - Consider total return including capital growth

Use the CanvasEstate dashboard to track your rental yields across your entire portfolio and identify opportunities for improvement.

---

*This guide is for educational purposes only and does not constitute financial advice. Consult with qualified professionals before making investment decisions.*`,
          author: 'Michael Chen, Property Analyst',
          publishedAt: new Date(Date.now() - 86400000 * 7).toISOString(), // 7 days ago
          tags: ['Rental Yield', 'Investment Analysis', 'Cash Flow', 'Education'],
          status: 'published',
          version: 1,
          updatedAt: { seconds: Date.now() / 1000, nanoseconds: 0 },
          updatedBy: 'system'
        },
        {
          id: '4',
          slug: 'negative-gearing-explained-2024-tax-guide',
          title: 'Negative Gearing Explained: Your 2024-25 Australian Tax Guide',
          excerpt: 'Everything you need to know about negative gearing, how it works, the tax benefits, and strategies to maximise your deductions in the current financial year.',
          content: `# Negative Gearing Explained: Your 2024-25 Australian Tax Guide

Negative gearing is one of the most discussed topics in Australian property investment, yet many investors don't fully understand how to maximise its benefits or when it makes strategic sense. This comprehensive guide breaks down everything you need to know.

## What is Negative Gearing?

Negative gearing occurs when your investment property expenses exceed your rental income, creating a loss. Under Australian tax law, this loss can be offset against your other income (such as salary), reducing your overall tax liability.

**Simple Example:**
- Annual Rental Income: $25,000
- Annual Expenses (interest, rates, maintenance, etc.): $35,000
- **Net Loss: -$10,000**

This $10,000 loss can be deducted from your taxable income, reducing your tax bill.

---

## How Does Negative Gearing Reduce Tax?

The tax benefit depends on your marginal tax rate. Here's how the benefit scales for a $10,000 investment loss in 2024-25:

| Taxable Income | Marginal Tax Rate | Tax Saving |
|---------------|-------------------|------------|
| $18,201 - $45,000 | 16% | $1,600 |
| $45,001 - $135,000 | 30% | $3,000 |
| $135,001 - $190,000 | 37% | $3,700 |
| Over $190,000 | 45% | $4,500 |

**Important:** Higher income earners receive more tax benefit from negative gearing. This is why it's particularly attractive for those in the 37% or 45% tax brackets.

---

## What Expenses Can Be Claimed?

### Immediately Deductible Expenses

These can be claimed in the year they're incurred:

| Expense | Typical Annual Cost |
|---------|-------------------|
| Loan interest | Varies (your biggest deduction) |
| Property management fees | 7-10% of rent |
| Council rates | $1,500-3,000 |
| Water rates | $800-1,500 |
| Insurance | $1,200-2,500 |
| Body corporate fees | $3,000-8,000 |
| Repairs and maintenance | Variable |
| Advertising for tenants | $200-500 |
| Bank charges | $50-200 |
| Land tax | Varies by state |
| Travel to property (limited) | Variable |

### Depreciation Deductions

Property investors can claim depreciation on two categories:

**1. Capital Works Deduction (Division 43)**
- Claim 2.5% per year of the construction cost
- For properties built after 15 September 1987
- Includes the building structure, walls, fixtures

**Example:** Building constructed for $400,000
Annual deduction: $400,000 × 2.5% = $10,000

**2. Plant and Equipment (Division 40)**
- Depreciable assets within the property
- Includes carpets, blinds, hot water systems, air conditioning
- Depreciation rates vary by asset type and effective life

**Important Note:** For second-hand residential properties purchased after 9 May 2017, you cannot claim plant and equipment depreciation on existing assets. You can only claim on new assets you add to the property.

---

## Negative Gearing Example: The Full Picture

Let's walk through a comprehensive example:

**Property Details:**
- Purchase Price: $600,000
- Loan Amount: $540,000 (90% LVR)
- Interest Rate: 6.5%
- Weekly Rent: $550

**Annual Income:**
- Rental Income: $550 × 52 = $28,600

**Annual Expenses:**
| Expense | Amount |
|---------|--------|
| Loan Interest | $35,100 |
| Property Management (8%) | $2,288 |
| Council Rates | $2,200 |
| Water Rates | $1,000 |
| Insurance | $1,800 |
| Repairs & Maintenance | $1,500 |
| Depreciation | $8,000 |
| **Total Expenses** | **$51,888** |

**Net Position:**
- Rental Income: $28,600
- Less Expenses: $51,888
- **Net Loss: -$23,288**

**Tax Benefit (37% bracket):** $23,288 × 37% = $8,617

**Actual Cash Cost:**
- Cash expenses (excluding depreciation): $43,888
- Cash income: $28,600
- Cash shortfall: $15,288
- Less tax benefit: $8,617
- **Net annual cost: $6,671**

So while the property costs you $6,671 per year in cash, you're building equity and benefiting from any capital growth.

---

## Is Negative Gearing Right for You?

### Negative Gearing Makes Sense When:

✅ You're in a high tax bracket (37% or above)
✅ The property has strong capital growth potential
✅ You have stable, high income to offset the losses
✅ You can comfortably afford the cash flow shortfall
✅ You're investing for the medium to long term (7+ years)
✅ Interest rates are manageable

### Negative Gearing May NOT Be Right When:

❌ You're in a low tax bracket (the tax benefit is minimal)
❌ You have unstable income or limited cash reserves
❌ The property has poor capital growth prospects
❌ You're close to retirement (less time to benefit from growth)
❌ You're highly leveraged already
❌ You're uncomfortable with ongoing cash outflows

---

## Negative Gearing vs Positive Gearing

**Positive Gearing:** When rental income exceeds expenses, creating taxable profit.

| Factor | Negative Gearing | Positive Gearing |
|--------|-----------------|------------------|
| Cash Flow | Negative (costs money each year) | Positive (generates income) |
| Tax Impact | Reduces taxable income | Increases taxable income |
| Typical Yields | Below 4-5% | Above 5-6% |
| Capital Growth | Often higher | Often lower |
| Best For | High income earners, growth focus | Retirees, income focus |

**Many successful portfolios include both** - negative geared properties for growth and positive geared properties for income.

---

## Strategies to Maximise Negative Gearing Benefits

### 1. Get a Depreciation Schedule
A qualified quantity surveyor can prepare a depreciation schedule for your property, typically costing $500-800. This can identify $5,000-15,000+ in annual deductions that many investors miss.

### 2. Structure Your Loans Correctly
- Keep investment debt separate from personal debt
- Consider interest-only loans for investment properties
- Don't pay down investment debt before personal debt

### 3. Time Your Expenses
Prepay interest or bring forward deductible expenses before 30 June if you expect lower income next financial year.

### 4. Keep Meticulous Records
- Track all property-related expenses
- Keep receipts for all repairs and maintenance
- Maintain a logbook for any property-related travel

### 5. Consider Split Loans
If you have a partner in a lower tax bracket, consider how loan structuring affects who claims the deductions.

---

## The Political Risk of Negative Gearing

It's important to acknowledge that negative gearing is periodically debated in Australian politics. Past proposals have included limiting negative gearing to new properties only.

**What investors should know:**
- Any changes would likely be grandfathered for existing investments
- Sudden abolition is politically unlikely
- Monitor policy announcements during election cycles
- Diversify strategies to reduce reliance on any single tax benefit

---

## Key Takeaways

1. **Negative gearing reduces tax, not property costs** - You still need to fund the cash flow shortfall
2. **Benefits increase with your tax bracket** - Most beneficial for those earning over $135,000
3. **Capital growth is essential** - Negative gearing only makes sense if the property grows in value
4. **Depreciation is often overlooked** - Get a professional schedule prepared
5. **Cash flow still matters** - Ensure you can sustain negative cash flow during your holding period

---

*This guide provides general information only and is not tax advice. Always consult with a registered tax agent or accountant for advice specific to your situation.*`,
          author: 'Jennifer Walsh, Tax Specialist',
          publishedAt: new Date(Date.now() - 86400000 * 10).toISOString(), // 10 days ago
          tags: ['Tax', 'Negative Gearing', 'Depreciation', 'Investment Strategy'],
          status: 'published',
          version: 1,
          updatedAt: { seconds: Date.now() / 1000, nanoseconds: 0 },
          updatedBy: 'system'
        },
        {
          id: '5',
          slug: 'brisbane-2032-olympics-property-investment-guide',
          title: 'Brisbane 2032 Olympics: Property Investment Opportunities & Suburb Guide',
          excerpt: 'Discover which Brisbane suburbs are poised to benefit most from the 2032 Olympic Games infrastructure investment and population growth.',
          content: `# Brisbane 2032 Olympics: Property Investment Opportunities & Suburb Guide

Brisbane's successful bid for the 2032 Olympic and Paralympic Games has put Southeast Queensland in the spotlight for property investors worldwide. With billions of dollars in infrastructure investment planned and strong interstate migration already underway, understanding where the opportunities lie is crucial.

## The 2032 Olympics: Investment at a Glance

**Key Facts:**
- Olympic Games: 23 July - 8 August 2032
- Paralympic Games: 24 August - 5 September 2032
- Estimated infrastructure spend: $7+ billion
- Venues across Brisbane, Gold Coast, and Sunshine Coast
- Expected visitor numbers: 1+ million during Games

---

## Major Infrastructure Projects

### 1. Brisbane Arena (Roma Street)
A new 17,000-seat indoor arena in the heart of Brisbane, replacing the outdated Entertainment Centre. This development will transform the Roma Street precinct.

**Nearby suburbs to watch:**
- Spring Hill
- Fortitude Valley
- Herston
- Petrie Terrace

### 2. The Gabba Redevelopment
The iconic Gabba will undergo significant upgrades, potentially including a new rectangular stadium. The Woolloongabba precinct is being reimagined as a vibrant urban hub.

**Nearby suburbs to watch:**
- Woolloongabba (ground zero for transformation)
- East Brisbane
- Kangaroo Point
- Coorparoo

### 3. Cross River Rail
This $7 billion underground rail project is the largest infrastructure project in Queensland's history, with new underground stations at:
- Boggo Road
- Woolloongabba
- Albert Street
- Roma Street

**Impact:** Suburbs along the rail corridor will see significantly improved connectivity, boosting property values.

### 4. Brisbane Metro
The metro busway system will transform public transport across Brisbane, with high-frequency services along dedicated corridors.

**Key corridors:**
- Cultural Centre to Royal Brisbane Hospital
- Eight Mile Plains to Roma Street

### 5. Gold Coast Investment
The Gold Coast will host several events, with significant venue upgrades planned at:
- Cbus Super Stadium (athletics warm-up)
- Gold Coast Aquatic Centre
- Broadbeach venues

---

## Top Suburbs to Watch: Brisbane

Based on infrastructure investment, current pricing, and growth potential, here are the suburbs we believe offer the best opportunities:

### Tier 1: Premium Growth Potential

**1. Woolloongabba**
- Median House Price: $1.3 million
- Why: Epicentre of Olympics transformation, new Cross River Rail station
- Risk: High price point, construction disruption short-term

**2. Albion**
- Median House Price: $1.1 million
- Why: Inner-north position, excellent connectivity, gentrifying
- Risk: Already priced for growth

**3. Kangaroo Point**
- Median Unit Price: $550,000
- Why: City views, Olympic precinct proximity, lifestyle appeal
- Risk: High-rise oversupply risk

### Tier 2: Strong Value Propositions

**4. Coorparoo**
- Median House Price: $1.2 million
- Why: Close to Gabba, established neighbourhood, good schools
- Risk: Premium pricing

**5. Morningside**
- Median House Price: $1.1 million
- Why: River access, improving infrastructure, family-friendly
- Risk: Flood-prone areas (check mapping carefully)

**6. Nundah**
- Median House Price: $1.0 million
- Why: Train line, village atmosphere, strong rental demand
- Risk: Some areas industrial

### Tier 3: Affordable Entry Points

**7. Northgate**
- Median House Price: $850,000
- Why: Airport Link, industrial-to-residential transformation
- Risk: Aircraft noise in parts

**8. Banyo**
- Median House Price: $830,000
- Why: Train station, improving streetscape, close to airport
- Risk: Less established than neighbours

**9. Zillmere**
- Median House Price: $720,000
- Why: Affordability, train station, council investment
- Risk: Socioeconomic challenges in parts

### Tier 4: Growth Corridors

**10. Ipswich Corridor (Springfield, Ripley)**
- Median House Price: $550,000-650,000
- Why: Major growth area, infrastructure investment, affordability
- Risk: Distance from CBD, supply concerns

---

## Top Suburbs to Watch: Gold Coast

**1. Robina**
- Median House Price: $950,000
- Why: Town centre, hospital, stadium proximity
- Risk: Already well-developed

**2. Varsity Lakes**
- Median House Price: $900,000
- Why: University, young demographic, lifestyle
- Risk: Student rental market volatility

**3. Pimpama**
- Median House Price: $680,000
- Why: Affordable, growth corridor, improving infrastructure
- Risk: Oversupply from new estates

---

## Investment Strategy for 2032

### Short-Term (2024-2026)
- Focus on established suburbs with confirmed infrastructure
- Lock in before major announcements drive prices higher
- Consider renovator opportunities in improving areas

### Medium-Term (2026-2030)
- Infrastructure benefits become visible
- Hold quality properties through construction disruption
- Consider selling underperforming assets to reinvest

### Olympics Period (2032)
- Exceptional short-term rental opportunities
- Airbnb/temporary accommodation demand surge
- Consider flexibility in lease timing

### Post-Olympics (2033+)
- Legacy infrastructure continues to support values
- Some speculative premium may deflate
- Focus on fundamentals: employment, transport, amenity

---

## Lessons from Past Olympics Host Cities

| City | Year | Property Impact |
|------|------|-----------------|
| Sydney | 2000 | Prices up 8% in host suburbs |
| London | 2012 | East London transformed, up 70%+ long-term |
| Rio | 2016 | Mixed results, economic challenges |
| Tokyo | 2020 | Limited impact due to COVID |

**Key lesson:** Long-term infrastructure is what drives lasting value, not the Games themselves.

---

## Risks to Consider

1. **Construction disruption** - Years of building work can temporarily affect liveability and values
2. **Oversupply** - Developer activity may exceed demand in some areas
3. **Economic conditions** - Global or national downturns could dampen growth
4. **Interest rates** - Continued high rates impact affordability
5. **Political risk** - Changes to Olympics scope or budget

---

## Key Takeaways

1. **Brisbane is a long-term growth story** - The Olympics accelerate existing trends
2. **Infrastructure is the key driver** - Buy near confirmed projects, not speculation
3. **Affordability still exists** - Outer suburbs offer entry points
4. **Don't over-rely on Olympics** - Fundamentals matter more than Games
5. **Consider timing** - Earlier purchases capture more growth

Southeast Queensland offers compelling opportunities for property investors willing to do their research and invest for the long term. The 2032 Olympics is a catalyst, but the underlying growth story is driven by population, infrastructure, and lifestyle factors that will persist well beyond the closing ceremony.

---

*Research current as of December 2024. Property data from CoreLogic and local sources. This is general information only - conduct your own due diligence.*`,
          author: 'CanvasEstate Research Team',
          publishedAt: new Date(Date.now() - 86400000 * 14).toISOString(), // 14 days ago
          tags: ['Brisbane', 'Olympics 2032', 'Infrastructure', 'Suburb Analysis'],
          status: 'published',
          version: 1,
          updatedAt: { seconds: Date.now() / 1000, nanoseconds: 0 },
          updatedBy: 'system'
        }
      ];

      // Save samples to local storage so edits persist
      localStorage.setItem('blog_posts', JSON.stringify(samplePosts));
      return samplePosts;

    } catch (error) {
      console.error('Error getting blog posts:', error);
      return [];
    }
  }

  /**
   * Get single blog post by slug
   */
  async getBlogPost(slug: string): Promise<BlogPost | null> {
    try {
      const posts = await this.getBlogPosts();
      return posts.find(p => p.slug === slug) || null;
    } catch (error) {
      console.error(`Error getting blog post ${slug}:`, error);
      return null;
    }
  }

  /**
   * Save blog post (create or update)
   */
  async saveBlogPost(post: BlogPost): Promise<void> {
    try {
      const posts = await this.getBlogPosts();
      const index = posts.findIndex(p => p.id === post.id);

      if (index >= 0) {
        posts[index] = { ...post, updatedAt: { seconds: Date.now() / 1000, nanoseconds: 0 } };
      } else {
        posts.push({ ...post, createdAt: { seconds: Date.now() / 1000, nanoseconds: 0 } } as any);
      }

      localStorage.setItem('blog_posts', JSON.stringify(posts));

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error saving blog post:', error);
      throw error;
    }
  }

  /**
   * Delete blog post
   */
  async deleteBlogPost(id: string): Promise<void> {
    try {
      const posts = await this.getBlogPosts();
      const filteredPosts = posts.filter(p => p.id !== id);
      localStorage.setItem('blog_posts', JSON.stringify(filteredPosts));

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error deleting blog post:', error);
      throw error;
    }
  }
}

export const adminService = new AdminService();

